import React, { useState, useEffect, forwardRef, useRef, useContext } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Select from "react-select";
import { getAllDepartments } from "../../../../api/api";
import {
  createSalaryApprovalV2,
  getSalaryApprovalDetailV2,
  getSalaryApprovalsV2,
  getSalaryDepartmentPreviewV2,
  patchSalaryApprovalPortalCache,
} from "../../../../api/timesheetApi";
import SalaryApprovalModal from "./SalaryApprovalModal";
import ApprovalStatusInfo from "./ApprovalStatusInfo";
import SalaryCalculationDrawer from "./SalaryCalculationDrawer";
import FinansistPushActions from "./FinansistPushActions";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import { hasFinansistPushRole } from "../../../../utils/finansistPayrollPush";
import "./SalaryDetail.css";

registerLocale("ru", ru);
setDefaultLocale("ru");

const parseNumericPrefix = (label = "") => {
  const match = String(label)
    .trim()
    .match(/^(\d+(?:\.\d+)*)/);
  if (!match) return null;
  return match[1].split(".").map((part) => Number(part));
};

const compareDepartmentLabels = (aLabel = "", bLabel = "") => {
  const aPrefix = parseNumericPrefix(aLabel);
  const bPrefix = parseNumericPrefix(bLabel);

  if (aPrefix && bPrefix) {
    const maxLength = Math.max(aPrefix.length, bPrefix.length);
    for (let i = 0; i < maxLength; i += 1) {
      const aPart = aPrefix[i];
      const bPart = bPrefix[i];
      if (aPart === undefined) return -1;
      if (bPart === undefined) return 1;
      if (aPart !== bPart) return aPart - bPart;
    }
  } else if (aPrefix && !bPrefix) {
    return -1;
  } else if (!aPrefix && bPrefix) {
    return 1;
  }

  return aLabel.localeCompare(bLabel, "ru", {
    numeric: true,
    sensitivity: "base",
  });
};

const compareEmployeeNames = (a, b) =>
  (a?.name || "").localeCompare(b?.name || "", "ru", {
    sensitivity: "base",
  });

const MONEY_COSTS_TO_API_FIELDS = {
  businessTrips: "business_trip_cost",
  annualLeave: "annual_vacation_cost",
  unpaidLeave: "unpaid_leave_cost",
  sickLeave: "sick_leave_cost",
  substitution: "substitution_cost",
};
const PAID_TO_API_FIELD = "paid";

function collectSalaryPortalCachePayloads({
  salaryData,
  initialSalaryData,
  year,
  month,
  departmentId,
}) {
  if (
    !salaryData ||
    !initialSalaryData ||
    departmentId === undefined ||
    departmentId === null
  ) {
    return [];
  }
  const initialRowsById = new Map(
    (initialSalaryData.groups || [])
      .flatMap((group) => group.rows || [])
      .map((row) => [row.id, row]),
  );
  const currentRows = (salaryData.groups || []).flatMap(
    (group) => group.rows || [],
  );
  const payloads = [];

  currentRows.forEach((row) => {
    const initialRow = initialRowsById.get(row.id);
    if (!initialRow) return;

    const changes = {};
    Object.entries(MONEY_COSTS_TO_API_FIELDS).forEach(
      ([moneyField, apiField]) => {
        const currentValue = Number(row.moneyCosts?.[moneyField] || 0);
        const initialValue = Number(initialRow.moneyCosts?.[moneyField] || 0);
        if (currentValue !== initialValue) {
          changes[apiField] = currentValue;
        }
      },
    );
    const currentPaid = Number(row.paid || 0);
    const initialPaid = Number(initialRow.paid || 0);
    if (currentPaid !== initialPaid) {
      changes[PAID_TO_API_FIELD] = currentPaid;
    }

    if (Object.keys(changes).length > 0) {
      payloads.push({
        user_id: row.id,
        department_id: Number(departmentId),
        year,
        month,
        changes,
        reason: "Ручная корректировка в Salary Detail",
      });
    }
  });

  return payloads;
}

const SalaryDetail = ({
  department,
  onBack,
  initialMonth,
  readOnlyMode = false,
  approvalId = null,
  backButtonLabel = "Заработная плата",
  customActions = null,
  /** Список «Заработная плата»: месяц только на предыдущем экране, без второго выбора */
  lockMonthToInitial = false,
  paymentsDateFrom = null,
  paymentsDateTo = null,
}) => {
  const { userData } = useContext(UserContext);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(
    initialMonth || new Date(),
  );
  const [departments, setDepartments] = useState([]);
  const [salaryData, setSalaryData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isTimesheetExpanded, setIsTimesheetExpanded] = useState(false);
  const [isMoneyCostsExpanded, setIsMoneyCostsExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // {employeeId, fieldName}
  const [tempValues, setTempValues] = useState({}); // {employeeId-fieldName: value}
  // Сохраняем данные о согласовании из переданного department
  const [approvalData, setApprovalData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  // Сохраняем изначальные данные для возможности сброса
  const [initialSalaryData, setInitialSalaryData] = useState(null);
  const [isTableDragging, setIsTableDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [calculationDrawerContext, setCalculationDrawerContext] =
    useState(null);
  const normalizeStatus = (status) => {
    switch ((status || "").toLowerCase()) {
      case "approved":
      case "согласован":
        return "Согласован";
      case "rejected":
      case "declined":
      case "отклонено":
      case "не согласован":
        return "Отклонено";
      case "pending":
      case "на рассмотрении":
      default:
        return "На рассмотрении";
    }
  };

  const formatPersonShortName = (person) => {
    if (!person) return "-";
    const surname = person.surname || "";
    const firstInitial = person.name?.[0] ? `${person.name[0]}.` : "";
    return `${surname} ${firstInitial}`.trim() || "-";
  };

  const tableContainerRef = useRef(null);
  const tableDragRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });

  const isInteractiveTableElement = (target) => {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest(
        "button, input, textarea, select, a, [role='button'], [data-no-table-drag='true']",
      ),
    );
  };

  const handleTableMouseDown = (event) => {
    if (event.button !== 0 || isInteractiveTableElement(event.target)) {
      return;
    }

    const container = tableContainerRef.current;
    if (!container) return;

    tableDragRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
      moved: false,
    };
    setIsTableDragging(true);
  };

  const handleTableMouseMove = (event) => {
    const container = tableContainerRef.current;
    if (!container || !tableDragRef.current.isDragging) return;

    const deltaX = event.clientX - tableDragRef.current.startX;
    if (Math.abs(deltaX) > 3) {
      tableDragRef.current.moved = true;
    }

    container.scrollLeft = tableDragRef.current.startScrollLeft - deltaX;
    event.preventDefault();
  };

  const stopTableDragging = () => {
    tableDragRef.current.isDragging = false;
    tableDragRef.current.moved = false;
    setIsTableDragging(false);
  };

  const handleTableClickCapture = (event) => {
    if (tableDragRef.current.moved) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  // Обновляем selectedMonth при изменении initialMonth
  useEffect(() => {
    if (initialMonth) {
      setSelectedMonth(initialMonth);
    }
  }, [initialMonth]);

  // Сохраняем данные о согласовании при получении department из пропсов (при клике на строку)
  useEffect(() => {
    if (department && department.approvers) {
      // Сохраняем данные о согласовании (approvers, initiator, submissionDate)
      setApprovalData({
        approvers: department.approvers || [],
        initiator: department.initiator,
        submissionDate: department.submissionDate,
        approvalId: department.approvalId ?? null,
      });
    }
    // Не сбрасываем approvalData здесь, так как он будет обновлен в другом useEffect
  }, [department]);

  // Загрузка отделов
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const allDepartments = await getAllDepartments();
        const activeDepartments = (
          Array.isArray(allDepartments) ? allDepartments : []
        )
          .filter((department) => department.is_active !== false)
          .map((department) => ({
            value: department.id ?? department.department_id,
            label: department.name ?? department.department_name,
          }))
          .filter((department) => department.value && department.label)
          .sort((a, b) => compareDepartmentLabels(a.label, b.label));

        setDepartments(activeDepartments);

        // Устанавливаем выбранный отдел
        if (department) {
          const dept = activeDepartments.find((d) => d.value === department.id);
          if (dept) {
            setSelectedDepartment(dept);
          }
        } else {
          // В режиме создания оставляем пустой выбор до явного выбора пользователя
          setSelectedDepartment(null);
        }
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
      }
    };

    loadDepartments();
  }, [department]);

  // Преобразование превью в формат компонента
  const transformPreviewToComponentFormat = (previewData) => {
    if (!previewData?.table_data) return null;

    const groups = (previewData.table_data.groups || []).map((group) => ({
      id: group.group_id,
      name: group.group_name,
      totals: {
        totalSalary: group.totals?.total_salary || 0,
        totalPaid: group.totals?.total_paid || 0,
        totalRemainder: group.totals?.total_remainder || 0,
      },
      rows: (group.rows || []).map((row) => ({
        id: row.user_id,
        name: row.full_name || "Неизвестно",
        salary: row.total_salary || 0,
        paid: row.paid || 0,
        remaining: row.remainder || 0,
        sickLeaveDays: row.sick_leave_days,
        timesheet: {
          workingDays: row.workdays || 0,
          weekendsAndHolidays: row.weekends_holidays || 0,
          daysWorked: row.worked_days || 0,
          workingHours: row.work_hours || 0,
          hoursWorked: row.worked_hours || 0,
          businessTrips: row.business_trip_days || 0,
          annualLeave: row.annual_vacation_days || 0,
          unpaidLeave: row.unpaid_leave_days || 0,
          substitution:
            (row.substitution_days || 0) +
            (row.maternity_leave_days || 0) +
            (row.sick_leave_days || 0),
        },
        moneyCosts: {
          baseSalary: row.base_salary || 0,
          workdays: row.workdays_cost || 0,
          weekendsAndHolidays: row.weekends_holidays_cost || 0,
          daysWorked: row.worked_days_cost || 0,
          workingHours: row.work_hours_cost || 0,
          hoursWorked: row.worked_hours_cost || 0,
          businessTrips: row.business_trip_cost || 0,
          annualLeave: row.annual_vacation_cost || 0,
          unpaidLeave: row.unpaid_leave_cost || 0,
          sickLeave:
            (row.maternity_leave_cost || 0) + (row.sick_leave_cost || 0),
          substitution: row.substitution_cost || 0,
        },
      })).sort(compareEmployeeNames),
    }));

    return {
      departmentName:
        previewData.department?.name ||
        previewData.table_data.department?.name ||
        "Неизвестное отделение",
      employeeCount: previewData.employees_count || 0,
      groups,
      totalAmount: previewData.total_salary || 0,
      paidAmount: previewData.total_paid || 0,
      remainingAmount: previewData.total_remainder || 0,
    };
  };

  // Загрузка данных зарплаты и данных о согласовании
  useEffect(() => {
    if (!selectedDepartment) {
      setSalaryData(null);
      setInitialSalaryData(null);
      setError(null);
      return;
    }

    const loadSalaryData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let sourceData = null;

        if (readOnlyMode && approvalId) {
          sourceData = await getSalaryApprovalDetailV2(approvalId);
        } else {
          const year = selectedMonth.getFullYear();
          const month = selectedMonth.getMonth() + 1;
          sourceData = await getSalaryDepartmentPreviewV2(
            selectedDepartment.value,
            {
              year,
              month,
            },
          );
        }

        const transformedData = transformPreviewToComponentFormat(sourceData);
        if (!transformedData) {
          throw new Error("Не удалось преобразовать данные превью");
        }

        setInitialSalaryData(JSON.parse(JSON.stringify(transformedData)));
        setSalaryData(transformedData);

        const initialExpanded = {};
        transformedData.groups.forEach((group) => {
          initialExpanded[group.id] = false;
        });
        setExpandedGroups(initialExpanded);

        if (readOnlyMode && sourceData?.approval) {
          const approval = sourceData.approval;
          const mappedApprovers = (approval.steps || []).map((step, index) => ({
            id: step.id,
            role: `Согласующий ${index + 1}`,
            name: formatPersonShortName(step.approver),
            status: normalizeStatus(step.status),
            comment: step.comment || "",
            decidedAt: step.decided_at ? new Date(step.decided_at) : null,
          }));
          if (sourceData?.company_approval?.approver) {
            mappedApprovers.push({
              id: sourceData.company_approval.id
                ? `company-${sourceData.company_approval.id}`
                : "company-approval",
              role: "Согласующий по компании",
              name: formatPersonShortName(sourceData.company_approval.approver),
              status: normalizeStatus(sourceData.company_approval.status),
              comment: sourceData.company_approval.comment || "",
              decidedAt: sourceData.company_approval.approved_at
                ? new Date(sourceData.company_approval.approved_at)
                : null,
            });
          }
          setApprovalData({
            approvers: mappedApprovers,
            initiator: formatPersonShortName(approval.initiator),
            submissionDate: approval.created_at
              ? new Date(approval.created_at)
              : null,
            approvalId,
          });
        } else {
          // В режиме создания/редактирования проверяем, есть ли уже согласование
          // для выбранного отдела и периода, чтобы корректно блокировать повторную отправку.
          try {
            const existingApprovals = await getSalaryApprovalsV2({
              department_id: selectedDepartment.value,
              year: selectedMonth.getFullYear(),
              month: selectedMonth.getMonth() + 1,
              offset: 0,
              limit: 20,
            });
            const existingApproval = (existingApprovals || []).find(
              (item) =>
                Number(item?.department?.id) ===
                  Number(selectedDepartment.value) &&
                Number(item?.year) === Number(selectedMonth.getFullYear()) &&
                Number(item?.month) === Number(selectedMonth.getMonth() + 1),
            );

            if (existingApproval) {
              const mappedApprovers = (existingApproval.steps || []).map(
                (step, index) => ({
                  id: step.id,
                  role: `Согласующий ${index + 1}`,
                  name: formatPersonShortName(step.approver),
                  status: normalizeStatus(step.status),
                  comment: step.comment || "",
                  decidedAt: step.decided_at ? new Date(step.decided_at) : null,
                }),
              );
              if (existingApproval?.company_approval?.approver) {
                mappedApprovers.push({
                  id: existingApproval.company_approval.id
                    ? `company-${existingApproval.company_approval.id}`
                    : "company-approval",
                  role: "Согласующий по компании",
                  name: formatPersonShortName(
                    existingApproval.company_approval.approver,
                  ),
                  status: normalizeStatus(
                    existingApproval.company_approval.status,
                  ),
                  comment: existingApproval.company_approval.comment || "",
                  decidedAt: existingApproval.company_approval.approved_at
                    ? new Date(existingApproval.company_approval.approved_at)
                    : null,
                });
              }
              setApprovalData({
                approvers: mappedApprovers,
                initiator: formatPersonShortName(existingApproval.initiator),
                submissionDate: existingApproval.created_at
                  ? new Date(existingApproval.created_at)
                  : null,
                approvalId: existingApproval.id,
              });
            } else {
              setApprovalData(null);
            }
          } catch (approvalCheckError) {
            console.error(
              "Ошибка проверки существующего согласования зарплаты:",
              approvalCheckError,
            );
            setApprovalData(null);
          }
        }
      } catch (err) {
        console.error("Ошибка при загрузке данных зарплаты:", err);
        setError(err.message || "Ошибка при загрузке данных");
        setSalaryData(null);
        setInitialSalaryData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadSalaryData();
  }, [selectedDepartment, selectedMonth, readOnlyMode, approvalId]);

  const formatMonthYear = (date) => {
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const isMonthPickerLocked = readOnlyMode || lockMonthToInitial;

  // Кастомный input для DatePicker с форматированием в именительном падеже
  const CustomDateInput = forwardRef(({ value, onClick, disabled }, ref) => (
    <input
      type="text"
      value={formatMonthYear(selectedMonth)}
      onClick={disabled ? undefined : onClick}
      ref={ref}
      readOnly
      disabled={disabled}
      className="salary-detail__datepicker"
      aria-label="Период расчёта"
    />
  ));
  CustomDateInput.displayName = "CustomDateInput";

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("ru-RU").format(amount);
  };

  const hasPositiveAmount = (amount) => Number(amount) > 0;
  const calcButtonFields = new Set([
    "annualLeave",
    "sickLeave",
    "substitution",
  ]);

  const handleCalculationsClick = (payload) => {
    setCalculationDrawerContext(payload);
  };

  const renderAmount = (amount) => <>{formatCurrency(amount)} тг</>;

  // Определение статуса согласования
  const getApprovalStatus = () => {
    if (
      !approvalData ||
      !approvalData.approvers ||
      approvalData.approvers.length === 0
    ) {
      return "neutral"; // Нейтральный статус
    }

    const statuses = approvalData.approvers.map((a) => a.status);
    const hasRejected = statuses.some((s) => s === "Отклонено");
    const hasPending = statuses.some((s) => s === "На рассмотрении");
    const allApproved = statuses.every((s) => s === "Согласован");

    if (hasRejected) {
      return "rejected";
    } else if (allApproved) {
      return "approved";
    } else if (hasPending) {
      return "pending";
    }
    return "neutral";
  };

  const approvalStatus = getApprovalStatus();
  const isEditable = !readOnlyMode && approvalStatus === "neutral";
  const canPushToFinansist =
    !customActions &&
    !readOnlyMode &&
    approvalStatus === "approved" &&
    Boolean(approvalData?.approvalId) &&
    hasFinansistPushRole(userData);

  // Несохранённые правки (moneyCosts + paid), сопоставление строк по id
  const hasChanges = () => {
    if (!salaryData || !initialSalaryData) return false;

    const initialRowsById = new Map(
      (initialSalaryData.groups || [])
        .flatMap((group) => group.rows || [])
        .map((row) => [row.id, row]),
    );
    const currentRows = (salaryData.groups || []).flatMap(
      (group) => group.rows || [],
    );

    return currentRows.some((row) => {
      const initialRow = initialRowsById.get(row.id);
      if (!initialRow) return true;

      const moneyChanged = Object.keys(MONEY_COSTS_TO_API_FIELDS).some(
        (key) =>
          Number(row.moneyCosts?.[key] || 0) !==
          Number(initialRow.moneyCosts?.[key] || 0),
      );
      const paidChanged =
        Number(row.paid || 0) !== Number(initialRow.paid || 0);
      return moneyChanged || paidChanged;
    });
  };

  const persistPortalCacheEdits = async ({ silent = false } = {}) => {
    if (!salaryData || !initialSalaryData || isSaving) return false;

    if (!selectedDepartment?.value) {
      window.alert("Выберите отдел перед сохранением.");
      return false;
    }

    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth() + 1;
    const payloads = collectSalaryPortalCachePayloads({
      salaryData,
      initialSalaryData,
      year,
      month,
      departmentId: selectedDepartment.value,
    });

    if (payloads.length === 0) return false;

    try {
      setIsSaving(true);
      await Promise.all(
        payloads.map((payload) => patchSalaryApprovalPortalCache(payload)),
      );
      setInitialSalaryData(JSON.parse(JSON.stringify(salaryData)));
      if (!silent) {
        setSuccessMessage("Изменения успешно сохранены");
        setShowSuccessPopup(true);
        setTimeout(() => setShowSuccessPopup(false), 2500);
      }
      return true;
    } catch (saveErr) {
      console.error("Ошибка сохранения изменений зарплаты:", saveErr);
      window.alert(
        saveErr?.message || "Не удалось сохранить изменения. Попробуйте снова.",
      );
      throw saveErr;
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprovalSubmit = async ({ approverIds, companyApproverId }) => {
    if (!selectedDepartment?.value) {
      throw new Error("Не выбран отдел для согласования");
    }
    if (!companyApproverId) {
      throw new Error("Не выбран согласующий по компании");
    }

    if (hasChanges()) {
      const saved = await persistPortalCacheEdits({ silent: true });
      if (!saved) {
        window.alert(
          "Не удалось сохранить правки перед согласованием. Проверьте данные и выбор отдела.",
        );
        throw new Error("Изменения не сохранены");
      }
    }

    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth() + 1;

    const payload = {
      year,
      month,
      department_id: selectedDepartment.value,
      approver_ids: approverIds,
      company_approver_id: companyApproverId,
    };
    const createdApproval = await createSalaryApprovalV2(payload);
    setShowApprovalModal(false);
    setSuccessMessage("Зарплата успешно отправлена на согласование");
    setShowSuccessPopup(true);
    setTimeout(() => setShowSuccessPopup(false), 2500);

    const mappedApprovers = (createdApproval?.steps || []).map(
      (step, index) => ({
        id: step.id,
        role: `Согласующий ${index + 1}`,
        name: formatPersonShortName(step.approver),
        status: normalizeStatus(step.status),
        comment: step.comment || "",
        decidedAt: step.decided_at ? new Date(step.decided_at) : null,
      }),
    );
    if (createdApproval?.company_approval?.approver) {
      mappedApprovers.push({
        id: createdApproval.company_approval.id
          ? `company-${createdApproval.company_approval.id}`
          : "company-approval",
        role: "Согласующий по компании",
        name: formatPersonShortName(createdApproval.company_approval.approver),
        status: normalizeStatus(createdApproval.company_approval.status),
        comment: createdApproval.company_approval.comment || "",
        decidedAt: createdApproval.company_approval.approved_at
          ? new Date(createdApproval.company_approval.approved_at)
          : null,
      });
    }

    setApprovalData({
      approvers: mappedApprovers,
      initiator: formatPersonShortName(createdApproval?.initiator),
      submissionDate: createdApproval?.created_at
        ? new Date(createdApproval.created_at)
        : new Date(),
      approvalId: createdApproval?.id ?? null,
    });
  };

  // Функция сброса к изначальным значениям
  const handleReset = () => {
    if (initialSalaryData) {
      // Восстанавливаем изначальные данные
      setSalaryData(JSON.parse(JSON.stringify(initialSalaryData)));
      // Очищаем все временные значения редактирования
      setTempValues({});
      setEditingCell(null);
      console.log("Данные сброшены к изначальным значениям");
    }
  };

  const handleSaveChanges = async () => {
    try {
      await persistPortalCacheEdits({ silent: false });
    } catch {
      /* сообщение уже показано в persistPortalCacheEdits */
    }
  };

  // Функции для редактирования ячеек
  const handleStartEdit = (employeeId, fieldName, currentValue) => {
    setEditingCell({ employeeId, fieldName });
    // При клике показываем либо старое значение (без форматирования), либо пустую строку
    // Преобразуем значение в число, затем в строку, чтобы убрать форматирование
    const numValue = parseFloat(currentValue) || 0;
    const initialValue = numValue !== 0 ? String(numValue) : "";
    setTempValues({
      ...tempValues,
      [`${employeeId}-${fieldName}`]: initialValue,
    });

    // Выделяем значение один раз при входе в редактирование.
    window.requestAnimationFrame(() => {
      const input = document.getElementById(
        `salary-edit-input-${employeeId}-${fieldName}`,
      );
      if (input) {
        input.select();
      }
    });
  };

  const applyEmployeeMoneyFieldUpdate = (prev, employeeId, fieldName, numValue) => {
    if (!prev) return prev;

    const updatedGroups = (prev.groups || []).map((group) => {
      const updatedRows = (group.rows || []).map((emp) => {
        if (Number(emp.id) !== Number(employeeId)) return emp;

        const isPaidField = fieldName === "paid";
        const updatedMoneyCosts = isPaidField
          ? { ...emp.moneyCosts }
          : {
              ...emp.moneyCosts,
              [fieldName]: numValue,
            };
        const updatedPaid = isPaidField ? numValue : emp.paid || 0;

        const totalSalary =
          (updatedMoneyCosts.hoursWorked || 0) +
          (updatedMoneyCosts.businessTrips || 0) +
          (updatedMoneyCosts.annualLeave || 0) +
          (updatedMoneyCosts.unpaidLeave || 0) +
          (updatedMoneyCosts.sickLeave || 0) +
          (updatedMoneyCosts.substitution || 0);

        return {
          ...emp,
          moneyCosts: updatedMoneyCosts,
          salary: totalSalary,
          paid: updatedPaid,
          remaining: totalSalary - updatedPaid,
        };
      });

      return {
        ...group,
        rows: updatedRows,
        totals: {
          totalSalary: updatedRows.reduce(
            (sum, emp) => sum + (emp.salary || 0),
            0,
          ),
          totalPaid: updatedRows.reduce(
            (sum, emp) => sum + (emp.paid || 0),
            0,
          ),
          totalRemainder: updatedRows.reduce(
            (sum, emp) => sum + (emp.remaining || 0),
            0,
          ),
        },
      };
    });

    const allRows = updatedGroups.flatMap((group) => group.rows || []);
    const totalAmount = allRows.reduce(
      (sum, emp) => sum + (emp.salary || 0),
      0,
    );
    const paidAmount = allRows.reduce((sum, emp) => sum + (emp.paid || 0), 0);
    const remainingAmount = allRows.reduce(
      (sum, emp) => sum + (emp.remaining || 0),
      0,
    );

    return {
      ...prev,
      groups: updatedGroups,
      employeeCount: allRows.length,
      totalAmount,
      paidAmount,
      remainingAmount,
    };
  };

  const handleSaveEdit = (employeeId, fieldName, newValue) => {
    const numValue = parseFloat(newValue) || 0;
    setSalaryData((prev) =>
      applyEmployeeMoneyFieldUpdate(prev, employeeId, fieldName, numValue),
    );
    setEditingCell(null);
    setTempValues((prev) => {
      const newTemp = { ...prev };
      delete newTemp[`${employeeId}-${fieldName}`];
      return newTemp;
    });
  };

  const handleCalculationSaved = ({ userId, fieldName, amount }) => {
    if (!userId || !fieldName) return;
    const numValue = Number(amount) || 0;
    setSalaryData((prev) =>
      applyEmployeeMoneyFieldUpdate(prev, userId, fieldName, numValue),
    );
    setInitialSalaryData((prev) =>
      applyEmployeeMoneyFieldUpdate(prev, userId, fieldName, numValue),
    );
    setCalculationDrawerContext((prev) => {
      if (!prev || Number(prev.userId) !== Number(userId)) return prev;
      const nextMoneyCosts = {
        ...(prev.moneyCosts || {}),
        [fieldName]: numValue,
      };
      const nextSalary =
        (nextMoneyCosts.hoursWorked || 0) +
        (nextMoneyCosts.businessTrips || 0) +
        (nextMoneyCosts.annualLeave || 0) +
        (nextMoneyCosts.unpaidLeave || 0) +
        (nextMoneyCosts.sickLeave || 0) +
        (nextMoneyCosts.substitution || 0);
      const paid = Number(prev.rowTotals?.paid ?? prev.paid) || 0;
      return {
        ...prev,
        moneyCosts: nextMoneyCosts,
        rowTotals: {
          ...(prev.rowTotals || {}),
          salary: nextSalary,
          remaining: nextSalary - paid,
        },
      };
    });
  };

  const handleCancelEdit = (employeeId, fieldName) => {
    setEditingCell(null);
    setTempValues((prev) => {
      const newTemp = { ...prev };
      delete newTemp[`${employeeId}-${fieldName}`];
      return newTemp;
    });
  };

  const handleTempValueChange = (employeeId, fieldName, value) => {
    // Разрешаем только числовые значения (включая десятичные) и пустую строку
    // Убираем все символы кроме цифр и точки
    // Убираем минус, так как min="0" и отрицательные значения не нужны
    const cleanedValue = value.replace(/[^\d.]/g, "");
    // Убираем лишние точки (оставляем только первую)
    const parts = cleanedValue.split(".");
    const finalValue =
      parts.length > 2
        ? parts[0] + "." + parts.slice(1).join("")
        : cleanedValue;
    setTempValues({
      ...tempValues,
      [`${employeeId}-${fieldName}`]: finalValue,
    });
  };

  // Компонент для редактируемой ячейки с деньгами
  const EditableMoneyCell = ({
    employeeId,
    fieldName,
    value,
    isEditable = true,
    calculationContext,
  }) => {
    const cellKey = `${employeeId}-${fieldName}`;
    const isEditing =
      editingCell?.employeeId === employeeId &&
      editingCell?.fieldName === fieldName;
    // При редактировании используем tempValue, иначе форматированное значение
    const tempValue = isEditing
      ? (tempValues[cellKey] ?? (value && value !== 0 ? String(value) : ""))
      : value;

    if (isEditing && isEditable) {
      return (
        <div
          data-no-table-drag="true"
          style={{
            position: "relative",
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <input
            id={`salary-edit-input-${employeeId}-${fieldName}`}
            type="text"
            inputMode="decimal"
            pattern="[0-9]*\.?[0-9]*"
            value={tempValue}
            onChange={(e) =>
              handleTempValueChange(employeeId, fieldName, e.target.value)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSaveEdit(employeeId, fieldName, tempValue);
              } else if (e.key === "Escape") {
                e.preventDefault();
                handleCancelEdit(employeeId, fieldName);
              }
            }}
            style={{
              width: "100%",
              maxWidth: "120px",
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
              boxSizing: "border-box",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#007bff";
            }}
            onBlur={() => {
              handleSaveEdit(employeeId, fieldName, tempValue);
            }}
            autoFocus
          />
        </div>
      );
    }

    const displayValue = formatCurrency(value);

    return (
      <div
        data-no-table-drag="true"
        style={{
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <span
          style={{
            fontSize: "14px",
            display: "inline-block",
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: isEditable ? "#F9FBFF" : "#F4F6FA",
            color: "#1F2937",
            cursor: isEditable ? "pointer" : "default",
            whiteSpace: "nowrap",
            opacity: isEditable ? 1 : 0.6,
          }}
          onClick={() => {
            if (isEditable) {
              handleStartEdit(employeeId, fieldName, value);
            }
          }}
        >
          {displayValue} тг
        </span>
        {hasPositiveAmount(value) &&
          !readOnlyMode &&
          calculationContext &&
          calcButtonFields.has(fieldName) && (
            <button
              type="button"
              className="salary-detail__calc-button"
              onClick={() =>
                handleCalculationsClick({
                  ...calculationContext,
                  userId: calculationContext.userId ?? employeeId,
                  fieldName,
                  amount: value,
                })
              }
            >
              Расчеты ↗
            </button>
          )}
      </div>
    );
  };

  // Определение статуса на основе сравнения рабочих дней и отработанных дней
  const getDaysStatus = (timesheet) => {
    if (!timesheet) return null;

    // Учитываем отпуска и больничные при сравнении
    // Если человек был в отпуске или на больничном, это нормально
    const totalDaysAccounted =
      timesheet.daysWorked +
      (timesheet.annualLeave || 0) +
      (timesheet.unpaidLeave || 0) +
      (timesheet.substitution ??
        (timesheet.maternityLeave || 0) +
          (timesheet.studyLeave || 0) +
          (timesheet.sickLeave || 0) +
          (timesheet.absences || 0));

    // Допускаем небольшую погрешность из-за округления (0.5 дня)
    const difference = Math.abs(totalDaysAccounted - timesheet.workingDays);

    // Если разница больше 0.5 дня, это может быть ошибкой
    if (difference > 0.5) {
      if (totalDaysAccounted < timesheet.workingDays) {
        return "error"; // Красный крестик - отработано меньше рабочих дней
      }
      if (totalDaysAccounted > timesheet.workingDays) {
        return "warning"; // Желтый треугольник - отработано больше рабочих дней
      }
    }

    return null;
  };

  // Определение общего статуса строки (для подсветки)
  const getRowStatus = (timesheet) => {
    return getDaysStatus(timesheet);
  };

  const renderStatusIcon = (timesheet, tooltipText) => {
    const status = getDaysStatus(timesheet);
    if (status === "error") {
      return (
        <span
          className="salary-detail__status-icon salary-detail__status-icon--error"
          title={tooltipText || "Отработано дней меньше рабочих дней"}
        >
          ✕
        </span>
      );
    }
    if (status === "warning") {
      return (
        <span
          className="salary-detail__status-icon salary-detail__status-icon--warning"
          title={tooltipText || "Отработано дней больше рабочих дней"}
        >
          ▲
        </span>
      );
    }
    return null;
  };

  const filteredGroups =
    salaryData?.groups
      ?.map((group) => ({
        ...group,
        rows: (group.rows || [])
          .filter((employee) => {
            if (!searchQuery) return true;
            const query = searchQuery.toLowerCase();
            return employee.name.toLowerCase().includes(query);
          })
          .sort(compareEmployeeNames),
      }))
      .filter((group) => !searchQuery || group.rows.length > 0) || [];

  const filteredEmployees = filteredGroups.flatMap((group) => group.rows || []);
  const totalColumns =
    1 + (isTimesheetExpanded ? 9 : 1) + (isMoneyCostsExpanded ? 11 : 1) + 3;

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      minWidth: "250px",
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "10px",
      zIndex: 1000, // Высокий z-index, чтобы меню было выше заголовков таблицы
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 1000, // Высокий z-index для menuPortal, если используется
    }),
  };

  return (
    <div className="salary-detail">
      {/* Кнопка назад */}
      <div className="salary-detail__header">
        <button className="salary-detail__back-button" onClick={onBack}>
          <span className="salary-detail__back-arrow">←</span>
          {backButtonLabel}
        </button>
      </div>

      {/* Селекты */}
      <div className="salary-detail__controls">
        <div className="salary-detail__control-group">
          <label className="salary-detail__label">Выбор отделения</label>
          <Select
            value={selectedDepartment}
            onChange={readOnlyMode ? undefined : setSelectedDepartment}
            options={departments}
            placeholder="Выберите отделение"
            styles={customSelectStyles}
            isDisabled={readOnlyMode}
          />
        </div>
        <div className="salary-detail__control-group">
          <label className="salary-detail__label">
            {isMonthPickerLocked ? "Период расчёта" : "Выбор даты"}
          </label>
          <DatePicker
            selected={selectedMonth}
            onChange={
              isMonthPickerLocked ? undefined : (date) => setSelectedMonth(date)
            }
            showMonthYearPicker
            locale="ru"
            calendarClassName="custom-calendar month-year-calendar"
            customInput={<CustomDateInput />}
            disabled={isMonthPickerLocked}
          />
          {lockMonthToInitial && !readOnlyMode && (
            <p className="salary-detail__locked-month-hint">
              Месяц совпадает с выбором на экране «{backButtonLabel}». Чтобы
              сменить период, нажмите «Назад» и выберите другой месяц.
            </p>
          )}
        </div>
      </div>

      {/* Информация о статусе согласования */}
      {approvalStatus !== "neutral" && approvalData && (
        <ApprovalStatusInfo
          department={approvalData}
          period={formatMonthYear(selectedMonth)}
        />
      )}

      {/* Индикатор загрузки */}
      {isLoading && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>Загрузка данных...</p>
        </div>
      )}

      {/* Сообщение об ошибке */}
      {error && !isLoading && (
        <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
          <p>Ошибка: {error}</p>
        </div>
      )}

      {/* Сводная информация */}
      {salaryData && !isLoading && (
        <div className="salary-detail__summary">
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Отделение:</span>
            <span className="salary-detail__summary-value">
              {salaryData.departmentName}
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Период:</span>
            <span className="salary-detail__summary-value">
              {formatMonthYear(selectedMonth)}
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">
              Количество сотрудников:
            </span>
            <span className="salary-detail__summary-value">
              {salaryData.employeeCount}
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">
              Общая сумма к выплате:
            </span>
            <span className="salary-detail__summary-value salary-detail__summary-value--amount">
              {formatCurrency(salaryData.totalAmount)} тг
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Выплачено:</span>
            <span className="salary-detail__summary-value salary-detail__summary-value--amount">
              {formatCurrency(salaryData.paidAmount)} тг
            </span>
          </div>
          <div className="salary-detail__summary-item">
            <span className="salary-detail__summary-label">Остаток:</span>
            <span className="salary-detail__summary-value salary-detail__summary-value--amount">
              {formatCurrency(salaryData.remainingAmount)} тг
            </span>
          </div>
        </div>
      )}

      {/* Поиск */}
      {salaryData && (
        <div className="salary-detail__search">
          <span className="salary-detail__search-icon">🔍</span>
          <input
            type="text"
            className="salary-detail__search-input"
            placeholder="Поиск"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Легенда */}
      {isTimesheetExpanded && (
        <div className="salary-detail__legend">
          <div className="salary-detail__legend-title">Обозначения:</div>
          <div className="salary-detail__legend-items">
            <div className="salary-detail__legend-item">
              <span className="salary-detail__status-icon salary-detail__status-icon--error">
                ✕
              </span>
              <span className="salary-detail__legend-text">
                Отработано дней меньше рабочих дней
              </span>
            </div>
            <div className="salary-detail__legend-item">
              <span className="salary-detail__status-icon salary-detail__status-icon--warning">
                ▲
              </span>
              <span className="salary-detail__legend-text">
                Отработано дней больше рабочих дней
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Таблица */}
      {salaryData && !isLoading && (
        <div
          ref={tableContainerRef}
          className={`salary-detail__table-container ${
            isTableDragging ? "salary-detail__table-container--dragging" : ""
          }`}
          onMouseDown={handleTableMouseDown}
          onMouseMove={handleTableMouseMove}
          onMouseUp={stopTableDragging}
          onMouseLeave={stopTableDragging}
          onClickCapture={handleTableClickCapture}
        >
          <table
            className={`salary-detail__table ${
              !isTimesheetExpanded && !isMoneyCostsExpanded
                ? "salary-detail__table--collapsed-sections"
                : ""
            }`}
          >
            <thead>
              <tr>
                <th rowSpan={2}>Отдел/ФИО</th>
                <th
                  colSpan={isTimesheetExpanded ? 9 : 1}
                  className="salary-detail__expandable-header"
                  onClick={() => setIsTimesheetExpanded(!isTimesheetExpanded)}
                  rowSpan={isTimesheetExpanded ? 1 : 2}
                >
                  Табелирование
                  <span
                    className={`salary-detail__expand-icon ${
                      isTimesheetExpanded
                        ? "salary-detail__expand-icon--expanded"
                        : ""
                    }`}
                  >
                    {isTimesheetExpanded ? "↓" : "→"}
                  </span>
                </th>
                <th
                  colSpan={isMoneyCostsExpanded ? 11 : 1}
                  className="salary-detail__expandable-header"
                  onClick={() => setIsMoneyCostsExpanded(!isMoneyCostsExpanded)}
                  rowSpan={isMoneyCostsExpanded ? 1 : 2}
                >
                  Затраты в деньгах (тг)
                  <span
                    className={`salary-detail__expand-icon ${
                      isMoneyCostsExpanded
                        ? "salary-detail__expand-icon--expanded"
                        : ""
                    }`}
                  >
                    {isMoneyCostsExpanded ? "↓" : "→"}
                  </span>
                </th>
                <th
                  colSpan={3}
                  className="salary-detail__summary-header"
                  rowSpan={1}
                >
                  Итоговые показатели
                </th>
              </tr>
              <tr>
                {isTimesheetExpanded && (
                  <>
                    <th className="salary-detail__table-header--vertical">
                      Рабочих дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Выходных и праздничных дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отработано дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Рабочих часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отработано часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Командировки
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отпуск / Компенсация
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отпуск б/с
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Замещение
                    </th>
                  </>
                )}
                {isMoneyCostsExpanded && (
                  <>
                    <th className="salary-detail__table-header--vertical">
                      Оклад
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Рабочих дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Выходных и праздничных дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отработано дней
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Рабочих часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отработано часов
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Командировки
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отпуск / Компенсация
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Отпуск б/с
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Больничный
                    </th>
                    <th className="salary-detail__table-header--vertical">
                      Замещение
                    </th>
                  </>
                )}
                <th className="salary-detail__summary-subheader">
                  Заработная плата
                </th>
                <th className="salary-detail__summary-subheader">Выплачено</th>
                <th className="salary-detail__summary-subheader">Остаток</th>
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((group) => (
                <React.Fragment key={`group-${group.id}`}>
                  {(() => {
                    const groupSalary = (group.rows || []).reduce(
                      (sum, employee) => sum + (employee.salary || 0),
                      0,
                    );
                    const groupPaid = (group.rows || []).reduce(
                      (sum, employee) => sum + (employee.paid || 0),
                      0,
                    );
                    const groupRemaining = (group.rows || []).reduce(
                      (sum, employee) => sum + (employee.remaining || 0),
                      0,
                    );
                    const isSummaryOnlyView =
                      !isTimesheetExpanded && !isMoneyCostsExpanded;

                    return (
                      <tr className="salary-detail__table-row salary-detail__group-row">
                        <td className="salary-detail__group-cell">
                          <button
                            type="button"
                            className="salary-detail__group-toggle"
                            onClick={() =>
                              setExpandedGroups((prev) => ({
                                ...prev,
                                [group.id]: !prev[group.id],
                              }))
                            }
                          >
                            <span className="salary-detail__group-chevron">
                              {expandedGroups[group.id] ? "▼" : "▶"}
                            </span>
                            <span className="salary-detail__group-title">
                              {group.name}
                            </span>
                          </button>
                        </td>
                        {isSummaryOnlyView ? (
                          <>
                            <td className="salary-detail__expandable-cell"></td>
                            <td className="salary-detail__expandable-cell"></td>
                            <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                              {formatCurrency(groupSalary)} тг
                            </td>
                            <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                              {formatCurrency(groupPaid)} тг
                            </td>
                            <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                              {formatCurrency(groupRemaining)} тг
                            </td>
                          </>
                        ) : (
                          <td
                            colSpan={Math.max(1, totalColumns - 1)}
                            className="salary-detail__group-fill-cell"
                          />
                        )}
                      </tr>
                    );
                  })()}
                  {expandedGroups[group.id] &&
                    (() => {
                      const groupMoneyTotals = (group.rows || []).reduce(
                        (acc, employee) => {
                          const moneyCosts = employee.moneyCosts || {};
                          return {
                            baseSalary:
                              acc.baseSalary + (moneyCosts.baseSalary || 0),
                            workdays: acc.workdays + (moneyCosts.workdays || 0),
                            weekendsAndHolidays:
                              acc.weekendsAndHolidays +
                              (moneyCosts.weekendsAndHolidays || 0),
                            daysWorked:
                              acc.daysWorked + (moneyCosts.daysWorked || 0),
                            workingHours:
                              acc.workingHours + (moneyCosts.workingHours || 0),
                            hoursWorked:
                              acc.hoursWorked + (moneyCosts.hoursWorked || 0),
                            businessTrips:
                              acc.businessTrips +
                              (moneyCosts.businessTrips || 0),
                            annualLeave:
                              acc.annualLeave + (moneyCosts.annualLeave || 0),
                            unpaidLeave:
                              acc.unpaidLeave + (moneyCosts.unpaidLeave || 0),
                            sickLeave:
                              acc.sickLeave + (moneyCosts.sickLeave || 0),
                            substitution:
                              acc.substitution + (moneyCosts.substitution || 0),
                          };
                        },
                        {
                          baseSalary: 0,
                          workdays: 0,
                          weekendsAndHolidays: 0,
                          daysWorked: 0,
                          workingHours: 0,
                          hoursWorked: 0,
                          businessTrips: 0,
                          annualLeave: 0,
                          unpaidLeave: 0,
                          sickLeave: 0,
                          substitution: 0,
                        },
                      );
                      const expandedGroupSalary = (group.rows || []).reduce(
                        (sum, employee) => sum + (employee.salary || 0),
                        0,
                      );
                      const expandedGroupPaid = (group.rows || []).reduce(
                        (sum, employee) => sum + (employee.paid || 0),
                        0,
                      );
                      const expandedGroupRemaining = (group.rows || []).reduce(
                        (sum, employee) => sum + (employee.remaining || 0),
                        0,
                      );

                      return (
                        <>
                          {group.rows.map((employee) => {
                            const moneyCalcContext = {
                              userId: employee.id,
                              employeeName: employee.name,
                              groupName: group.name,
                              departmentName: salaryData.departmentName,
                              timesheet: employee.timesheet,
                              sickLeaveDaysFromPreview: employee.sickLeaveDays,
                              rowTotals: {
                                salary: employee.salary,
                                paid: employee.paid,
                                remaining: employee.remaining,
                              },
                            };
                            const rowStatus = getRowStatus(employee.timesheet);
                            const rowClassName = `salary-detail__table-row salary-detail__employee-row ${
                              rowStatus === "warning"
                                ? "salary-detail__table-row--warning"
                                : rowStatus === "error"
                                  ? "salary-detail__table-row--error"
                                  : ""
                            }`;
                            return (
                              <tr
                                key={`${group.id}-${employee.id}`}
                                className={rowClassName}
                              >
                                <td className="salary-detail__employee-cell">
                                  <div className="salary-detail__name-cell salary-detail__name-cell--employee">
                                    <span className="salary-detail__name-text">
                                      {employee.name}
                                    </span>
                                  </div>
                                </td>
                                {isTimesheetExpanded && employee.timesheet ? (
                                  <>
                                    <td>{employee.timesheet.workingDays}</td>
                                    <td>
                                      {employee.timesheet.weekendsAndHolidays}
                                    </td>
                                    <td>
                                      {employee.timesheet.daysWorked}
                                      {renderStatusIcon(
                                        employee.timesheet,
                                        `Рабочих дней: ${employee.timesheet.workingDays}, Отработано: ${employee.timesheet.daysWorked}`,
                                      )}
                                    </td>
                                    <td>{employee.timesheet.workingHours}</td>
                                    <td>{employee.timesheet.hoursWorked}</td>
                                    <td>{employee.timesheet.businessTrips}</td>
                                    <td>{employee.timesheet.annualLeave}</td>
                                    <td>{employee.timesheet.unpaidLeave}</td>
                                    <td>
                                      {employee.timesheet.substitution ??
                                        (employee.timesheet.maternityLeave ||
                                          0) +
                                          (employee.timesheet.studyLeave || 0) +
                                          (employee.timesheet.sickLeave || 0) +
                                          (employee.timesheet.absences || 0)}
                                    </td>
                                  </>
                                ) : (
                                  <td className="salary-detail__expandable-cell"></td>
                                )}
                                {isMoneyCostsExpanded && employee.moneyCosts ? (
                                  <>
                                    <td className="salary-detail__amount-cell">
                                      {renderAmount(
                                        employee.moneyCosts.baseSalary,
                                      )}
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      {renderAmount(
                                        employee.moneyCosts.workdays,
                                      )}
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      {renderAmount(
                                        employee.moneyCosts.weekendsAndHolidays,
                                      )}
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      {renderAmount(
                                        employee.moneyCosts.daysWorked,
                                      )}
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      {renderAmount(
                                        employee.moneyCosts.workingHours,
                                      )}
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      {renderAmount(
                                        employee.moneyCosts.hoursWorked,
                                      )}
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      <EditableMoneyCell
                                        employeeId={employee.id}
                                        fieldName="businessTrips"
                                        value={
                                          employee.moneyCosts.businessTrips
                                        }
                                        isEditable={isEditable}
                                        calculationContext={moneyCalcContext}
                                      />
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      <EditableMoneyCell
                                        employeeId={employee.id}
                                        fieldName="annualLeave"
                                        value={employee.moneyCosts.annualLeave}
                                        isEditable={isEditable}
                                        calculationContext={moneyCalcContext}
                                      />
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      <EditableMoneyCell
                                        employeeId={employee.id}
                                        fieldName="unpaidLeave"
                                        value={employee.moneyCosts.unpaidLeave}
                                        isEditable={isEditable}
                                        calculationContext={moneyCalcContext}
                                      />
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      <EditableMoneyCell
                                        employeeId={employee.id}
                                        fieldName="sickLeave"
                                        value={employee.moneyCosts.sickLeave}
                                        isEditable={isEditable}
                                        calculationContext={moneyCalcContext}
                                      />
                                    </td>
                                    <td className="salary-detail__amount-cell">
                                      <EditableMoneyCell
                                        employeeId={employee.id}
                                        fieldName="substitution"
                                        value={employee.moneyCosts.substitution}
                                        isEditable={isEditable}
                                        calculationContext={moneyCalcContext}
                                      />
                                    </td>
                                  </>
                                ) : (
                                  <td className="salary-detail__expandable-cell"></td>
                                )}
                                <td className="salary-detail__amount-cell">
                                  {formatCurrency(employee.salary)} тг
                                </td>
                                <td className="salary-detail__amount-cell">
                                  <EditableMoneyCell
                                    employeeId={employee.id}
                                    fieldName="paid"
                                    value={employee.paid}
                                    isEditable={isEditable}
                                  />
                                </td>
                                <td className="salary-detail__amount-cell">
                                  {formatCurrency(employee.remaining)} тг
                                </td>
                              </tr>
                            );
                          })}
                          <tr className="salary-detail__table-row salary-detail__table-row--total">
                            <td className="salary-detail__total-label">
                              Итого по отделу
                            </td>
                            {isTimesheetExpanded ? (
                              <>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                                <td className="salary-detail__expandable-cell"></td>
                              </>
                            ) : (
                              <td className="salary-detail__expandable-cell"></td>
                            )}
                            {isMoneyCostsExpanded ? (
                              <>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.baseSalary)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.workdays)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(
                                    groupMoneyTotals.weekendsAndHolidays,
                                  )}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.daysWorked)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.workingHours)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.hoursWorked)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.businessTrips)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.annualLeave)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.unpaidLeave)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.sickLeave)}
                                </td>
                                <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                                  {renderAmount(groupMoneyTotals.substitution)}
                                </td>
                              </>
                            ) : (
                              <td className="salary-detail__expandable-cell"></td>
                            )}
                            <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                              {formatCurrency(expandedGroupSalary)} тг
                            </td>
                            <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                              {formatCurrency(expandedGroupPaid)} тг
                            </td>
                            <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                              {formatCurrency(expandedGroupRemaining)} тг
                            </td>
                          </tr>
                        </>
                      );
                    })()}
                </React.Fragment>
              ))}
              {/* Итоговая строка */}
              {salaryData && (
                <tr className="salary-detail__table-row salary-detail__table-row--total">
                  <td className="salary-detail__total-label">ИТОГО</td>
                  {isTimesheetExpanded && (
                    <>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                      <td className="salary-detail__expandable-cell"></td>
                    </>
                  )}
                  {!isTimesheetExpanded && (
                    <td className="salary-detail__expandable-cell"></td>
                  )}
                  {isMoneyCostsExpanded && (
                    <>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.baseSalary || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) => sum + (emp.moneyCosts?.workdays || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.weekendsAndHolidays || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.daysWorked || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.workingHours || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.hoursWorked || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.businessTrips || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.annualLeave || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.unpaidLeave || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.sickLeave || 0),
                            0,
                          ),
                        )}
                      </td>
                      <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                        {renderAmount(
                          filteredEmployees.reduce(
                            (sum, emp) =>
                              sum + (emp.moneyCosts?.substitution || 0),
                            0,
                          ),
                        )}
                      </td>
                    </>
                  )}
                  {!isMoneyCostsExpanded && (
                    <td className="salary-detail__expandable-cell"></td>
                  )}
                  <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                    {formatCurrency(salaryData.totalAmount)} тг
                  </td>
                  <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                    {formatCurrency(salaryData.paidAmount)} тг
                  </td>
                  <td className="salary-detail__amount-cell salary-detail__amount-cell--total">
                    {formatCurrency(salaryData.remainingAmount)} тг
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Кнопки действий */}
      {salaryData && (!readOnlyMode || customActions || canPushToFinansist) && (
        <div className="salary-detail__actions">
          {/* <button className="salary-detail__button salary-detail__button--print">
          <span className="salary-detail__button-icon">🖨️</span>
          Печать PDF
        </button> */}
          {hasChanges() && (
            <button
              className="salary-detail__button salary-detail__button--reset"
              onClick={handleReset}
              style={{
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
              }}
            >
              Сбросить
            </button>
          )}
          {!readOnlyMode && (
            <>
              <button
                className="salary-detail__button salary-detail__button--save"
                onClick={handleSaveChanges}
                disabled={!hasChanges() || isSaving}
                style={{
                  opacity: !hasChanges() || isSaving ? 0.6 : 1,
                  cursor: !hasChanges() || isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? "Сохранение..." : "Сохранить"}
              </button>
              <button
                className="salary-detail__button salary-detail__button--submit"
                onClick={() => setShowApprovalModal(true)}
                disabled={approvalStatus !== "neutral" || isSaving}
                style={{
                  opacity: approvalStatus !== "neutral" || isSaving ? 0.6 : 1,
                  cursor:
                    approvalStatus !== "neutral" || isSaving
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Отправить на согласование
              </button>
            </>
          )}
          {customActions}
          <FinansistPushActions
            approvalId={approvalData?.approvalId}
            calculationYear={selectedMonth.getFullYear()}
            month={selectedMonth.getMonth() + 1}
            visible={canPushToFinansist}
          />
        </div>
      )}

      {/* Модальное окно согласования */}
      <SalaryApprovalModal
        open={showApprovalModal}
        onClose={() => setShowApprovalModal(false)}
        onSubmit={handleApprovalSubmit}
        departmentName={salaryData?.departmentName}
        period={formatMonthYear(selectedMonth)}
      />

      <SalaryCalculationDrawer
        open={Boolean(calculationDrawerContext)}
        onClose={() => setCalculationDrawerContext(null)}
        onSaved={handleCalculationSaved}
        context={calculationDrawerContext}
        year={selectedMonth.getFullYear()}
        month={selectedMonth.getMonth() + 1}
        periodLabel={formatMonthYear(selectedMonth)}
      />

      {showSuccessPopup && (
        <div
          className="salary-detail__success-overlay"
          onClick={() => setShowSuccessPopup(false)}
        >
          <div
            className="salary-detail__success-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="salary-detail__success-icon" />
            <p className="salary-detail__success-text">{successMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryDetail;
